import { Box } from '@material-ui/core';
import React from 'react';

import { ReactComponent as CommentIcon } from '../../../shared/assets/icons/comment.svg';
import { ReactComponent as GlassesIcon } from '../../../shared/assets/icons/glasses.svg';
import { ReactComponent as LightningIcon } from '../../../shared/assets/icons/lightning.svg';
import { toTzMoment } from '../../../shared/lib';
import { getReservePersonName, getReserveItemState, tableColors } from '../lib';
import { lightTheme } from '../lib/theme';

import { Duo } from './duo';
import { ReserveFooterWidget } from './reserveFooter';
import { SvgIcon } from './svgIcon';
import { Tag } from './tag';

const { fg } = lightTheme;
const { badge } = lightTheme.components;

// Пустая функция для onClickCollapse, так как стрелка скрыта через forcedOpen
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

interface ReserveHorizontalCardProps {
  scheduleReserve: GM.ReservesSchemas.ScheduleReserve;
  tablesSchedule: GM.ReservesSchemas.TablesSchedule;
  restaurantTz?: string;
  isSelected?: boolean;
}

export const ReserveHorizontalCard: React.FC<ReserveHorizontalCardProps> = ({
  scheduleReserve,
  tablesSchedule,
  restaurantTz,
}) => {
  const person = getReservePersonName(scheduleReserve);
  const date = toTzMoment(scheduleReserve.date, restaurantTz);
  const timeStr = date.format('HH:mm');
  const state = getReserveItemState(
    scheduleReserve,
    tablesSchedule[scheduleReserve.tables[0].id],
    restaurantTz,
  );

  const isWarningMessage = ['WARNING', 'CONFLICT'].includes(state);
  const isPrepay = !!scheduleReserve.prepay?.amount;
  // const compact = true; // Используем компактный режим для горизонтальных карточек
  const compact = false;

  const renderPrepayTag = () => {
    if (!isPrepay) {
      return null;
    }

    const isPaid = scheduleReserve.state !== 'AWAIT_PREPAY';

    const stateColorName = !isPaid && !isWarningMessage ? 'WARNING' : state;
    const tagStyles: React.CSSProperties = {
      color: tableColors[stateColorName].head.color,
      background: tableColors[stateColorName].head.bg,
    };

    if (tableColors[stateColorName].head.stripe) {
      tagStyles['background'] = `repeating-linear-gradient(
            -45deg,
            ${tableColors[stateColorName].head.stripe} 0 4px,
            transparent 4px 8px
          ), ${tagStyles.background}`;
    }

    return (
      <Tag
        style={{
          ...tagStyles,
          fontSize: compact ? 10 : undefined,
          padding: compact ? '3px 6px' : '2px 9px',
        }}
      >
        Депозит
        {isPaid ? ' ' : ' не '}
        оплачен: {scheduleReserve.prepay.amount} ₽
      </Tag>
    );
  };

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Верхняя часть: тег с временем, депозит, предупреждения и бейджи */}
      <Box marginBottom={compact ? '4px' : '8px'} style={{ minHeight: 0, flexShrink: 0 }}>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={compact ? '2px' : '4px'}
        >
          <Box>
            <Tag
              style={{
                color: tableColors[state].head.color,
                background: tableColors[state].head.bg,
                fontSize: compact ? 11 : undefined,
                padding: compact ? '3px 7px' : '2px 9px',
              }}
            >
              <span style={{ fontWeight: 600 }}>{timeStr}</span>{' '}
              <span style={{ fontWeight: 400 }}>{date.format('DD.MM')}</span>
            </Tag>
          </Box>

          {scheduleReserve.comment && (
            <Box display="flex" style={{ flexShrink: 0 }}>
              <SvgIcon icon={CommentIcon} width={compact ? 12 : 14} height={compact ? 12 : 14} />
            </Box>
          )}
        </Box>

        {isPrepay && <Box mb={compact ? '2px' : '4px'}>{renderPrepayTag()}</Box>}

        {isWarningMessage && (
          <Box
            mb={compact ? '2px' : '4px'}
            color={tableColors[state].head.color}
            bgcolor={tableColors[state].head.bg}
            borderRadius={8}
            padding={compact ? '6px 10px' : '8px 12px'}
            fontSize={compact ? 12 : 14}
          >
            {state === 'WARNING' && <>На этот стол скоро должен прийти гость</>}
            {state === 'CONFLICT' && (
              <>Время резерва наступило, но&nbsp;за&nbsp;столом сидят другие гости</>
            )}
          </Box>
        )}

        {scheduleReserve.isAutoBooking && (
          <Tag
            style={{
              color: fg.default,
              backgroundColor: badge.muted.neutral.bg,
              marginRight: '4px',
              marginBottom: '4px',
              fontSize: compact ? 10 : undefined,
              padding: compact ? '2px 6px' : '2px 9px',
            }}
          >
            <Duo
              first={
                <SvgIcon icon={LightningIcon} width={compact ? 9 : 10} height={compact ? 11 : 14} />
              }
              second={
                <span style={{ fontSize: compact ? 10 : 12, fontWeight: 500 }}>
                  Мгновенный резерв
                </span>
              }
              gridColumnGap={compact ? 3 : 4}
              alignItems="center"
            />
          </Tag>
        )}

        {scheduleReserve.isBanquet && (
          <Tag
            style={{
              color: fg.default,
              backgroundColor: badge.muted.neutral.bg,
              marginRight: '4px',
              marginBottom: '4px',
              fontSize: compact ? 10 : undefined,
              padding: compact ? '2px 6px' : '2px 9px',
            }}
          >
            <Duo
              first={
                <SvgIcon icon={GlassesIcon} width={compact ? 11 : 14} height={compact ? 11 : 14} />
              }
              second={<span style={{ fontSize: compact ? 10 : 12, fontWeight: 500 }}>Банкет</span>}
              gridColumnGap={compact ? 3 : 4}
              alignItems="center"
            />
          </Tag>
        )}
      </Box>

      {/* Имя гостя */}
      {person && (
        <Box
          fontSize={compact ? 13 : 16}
          fontWeight={600}
          color={fg.default}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            lineHeight: 1.3,
          }}
        >
          {person}
        </Box>
      )}

      {/* Нижняя часть: ReserveFooterWidget (как в вертикальной версии) */}
      <Box style={{ marginTop: 'auto', flexShrink: 0 }}>
        <ReserveFooterWidget
          room={scheduleReserve.tables[0].hall}
          tables={scheduleReserve.tables.map((t) => t.number)}
          persons={scheduleReserve.persons}
          collapsed
          onClickCollapse={noop}
          // compact={compact}
          forcedOpen={true}
        />
      </Box>
    </Box>
  );
};
